import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getClientDays, type DaySummary } from '../../lib/refonteApi';
import { usePortalScope } from './portal/usePortalScope';
import ViewAsClientToggle from '../../components/refonte/ViewAsClientToggle';
import { SkeletonRows, Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const JOURS = ['L', 'M', 'M', 'J', 'V']; // jours ouvrés uniquement
// Date LOCALE (pas toISOString, qui décale d'un jour en fuseau positif comme Paris).
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const longDate = (s: string) => new Date(s + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const mondayFirst = (d: Date) => (d.getDay() + 6) % 7; // L=0 … D=6
const pct = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 100) : 0);

const Chip: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="flex items-baseline gap-1.5 rounded-md bg-panel-2 border border-line px-2.5 py-1">
    <span className="font-mono text-[15px] font-bold tabular-nums">{value}</span>
    <span className="font-mono text-[9px] uppercase tracking-widest text-faint">{label}</span>
  </div>
);

// Liste des résumés d'appels d'un ensemble de journées (le « travail effectué »).
const CallSummaries: React.FC<{ items: DaySummary['items'] }> = ({ items }) => (
  items.length === 0
    ? <p className="text-[12px] text-faint">Aucun résumé de conversation pour cette période.</p>
    : <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="text-[12px] border-b border-line pb-2 last:border-0">
            <div className="flex items-center gap-2 mb-0.5">
              <span className="font-semibold truncate">{it.company || 'Contact'}</span>
              {it.contact && <span className="text-faint text-[11px] truncate">· {it.contact}</span>}
            </div>
            <p className="text-dim leading-snug whitespace-pre-wrap">{it.summary}</p>
          </div>
        ))}
      </div>
);

// Portail client — JOURNÉES DE PROSPECTION. Agenda (calendrier) dérivé des appels réels
// (call_logs), automatique. Bascule Jour (pack) / Semaine. Le client voit le travail
// effectué : par jour cliquable dans le calendrier, ou agrégé par semaine.
const PortalJourneesPage: React.FC = () => {
  const scope = usePortalScope();
  const { clientId, clientName } = scope;

  const now = new Date();
  const [sp] = useSearchParams();
  const deepDay = sp.get('d'); // YYYY-MM-DD : jour préselectionné (lien depuis les résumés du dashboard)
  const deep = deepDay ? new Date(deepDay + 'T00:00:00') : null;
  const [year, setYear] = useState(deep ? deep.getFullYear() : now.getFullYear());
  const [month, setMonth] = useState(deep ? deep.getMonth() : now.getMonth()); // 0-11
  const [mode, setMode] = useState<'jour' | 'semaine'>('jour');
  const [days, setDays] = useState<DaySummary[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const range = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    return { from: iso(first), to: iso(last) };
  }, [year, month]);

  const load = useCallback(async () => {
    if (!clientId) { setLoading(false); return; }
    setLoading(true); setErr(null);
    try {
      const d = await getClientDays(clientId, range.from, range.to);
      setDays(d);
      setSelected(deepDay && d.some(x => x.date === deepDay) ? deepDay : (d[0]?.date ?? null)); // deep-link sinon la plus récente
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [clientId, range]);
  useEffect(() => { load(); }, [load]);

  const byDate = useMemo(() => new Map(days.map(d => [d.date, d])), [days]);
  const cQuery = clientId ? `?c=${clientId}${scope.viewAsClient ? '&asclient=1' : ''}` : (scope.viewAsClient ? '?asclient=1' : '');

  // Cellules du calendrier — jours ouvrés uniquement (Lun–Ven, 5 colonnes), en dates locales.
  const cells = useMemo(() => {
    const n = new Date(year, month + 1, 0).getDate();
    const out: (string | null)[] = [];
    for (let d = 1; d <= n; d++) {
      const date = new Date(year, month, d);
      const wd = mondayFirst(date);           // L=0 … D=6
      if (wd >= 5) continue;                  // on saute samedi/dimanche
      if (out.length === 0) for (let i = 0; i < wd; i++) out.push(null); // blancs de début
      out.push(iso(date));
    }
    while (out.length % 5 !== 0) out.push(null);
    return out;
  }, [year, month]);

  const maxConv = Math.max(1, ...days.map(d => d.conversations));
  const intensity = (c: number) => (c === 0 ? '' : c / maxConv > 0.66 ? 'bg-hook-rose/25' : c / maxConv > 0.33 ? 'bg-hook-rose/15' : 'bg-hook-rose/5');

  // Regroupement par semaine (lundi) pour la vue GTM.
  const weeks = useMemo(() => {
    const map = new Map<string, DaySummary & { end: string }>();
    for (const d of days) {
      const dt = new Date(d.date + 'T00:00:00');
      const monday = new Date(dt); monday.setDate(dt.getDate() - mondayFirst(dt));
      const key = iso(monday);
      let w = map.get(key);
      if (!w) { w = { date: key, end: iso(new Date(monday.getTime() + 6 * 86400000)), calls: 0, connected: 0, conversations: 0, rdv: 0, items: [], mailSent: 0, liSent: 0 }; map.set(key, w); }
      w.calls += d.calls; w.connected += d.connected; w.conversations += d.conversations; w.rdv += d.rdv; w.mailSent = (w.mailSent ?? 0) + (d.mailSent ?? 0); w.liSent = (w.liSent ?? 0) + (d.liSent ?? 0); w.items.push(...d.items);
    }
    return [...map.values()].sort((a, b) => b.date.localeCompare(a.date));
  }, [days]);

  const sel = selected ? byDate.get(selected) : null;
  const prevMonth = () => { const d = new Date(year, month - 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); };
  const nextMonth = () => { const d = new Date(year, month + 1, 1); setYear(d.getFullYear()); setMonth(d.getMonth()); };
  const monthTotals = days.reduce((a, d) => ({ conn: a.conn + d.connected, conv: a.conv + d.conversations, rdv: a.rdv + d.rdv, mail: a.mail + (d.mailSent ?? 0), li: a.li + (d.liSent ?? 0) }), { conn: 0, conv: 0, rdv: 0, mail: 0, li: 0 });
  const hasOutreach = monthTotals.mail + monthTotals.li > 0;

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Journées de prospection</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to={`/app/portail${cQuery}`} className="text-[12px] font-semibold text-dim hover:text-ink">← Tableau de bord</Link>
          {scope.isInternal && <ViewAsClientToggle on={scope.viewAsClient} onChange={scope.setViewAsClient} />}
          {(scope.isInternal || scope.options.length > 1) && (
            <select value={clientId ?? ''} onChange={e => scope.setClientId(e.target.value || null)}
              className="rounded-md border border-line-2 bg-panel px-2 py-1.5 text-[13px] max-w-[220px]">
              {!clientId && <option value="">— choisir —</option>}
              {scope.options.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          )}
        </div>
      </div>
      <p className="text-dim text-[13px] mb-4">
        Le travail effectué, jour par jour — généré automatiquement à partir des conversations
        {clientName && <span className="text-faint"> · {clientName}</span>}
      </p>

      {(err || scope.error) && <EmptyState tone="error" title="Chargement impossible" hint={err || scope.error || ''} className="mb-4" />}

      {scope.resolving ? <Skeleton className="h-[60vh] rounded-card" /> : !clientId ? (
        <EmptyState title="Aucun client rattaché" hint="Votre compte n'est associé à aucun client." />
      ) : (
        <>
          {/* Barre : navigation mois + bascule Jour/Semaine */}
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <button onClick={prevMonth} aria-label="Mois précédent" className="w-8 h-8 rounded-md border border-line-2 hover:border-ink">‹</button>
            <span className="text-[14px] font-bold capitalize min-w-[150px] text-center">{MOIS[month]} {year}</span>
            <button onClick={nextMonth} aria-label="Mois suivant" className="w-8 h-8 rounded-md border border-line-2 hover:border-ink">›</button>
            <span className="w-px h-6 bg-line-2 mx-1" />
            {(['jour', 'semaine'] as const).map(m => (
              <button key={m} onClick={() => setMode(m)}
                className={`text-[12px] font-semibold px-3 py-1.5 rounded-md border capitalize ${mode === m ? 'bg-ink text-white border-ink' : 'border-line-2 text-dim hover:border-ink'}`}>{m === 'jour' ? 'Jour' : 'Semaine'}</button>
            ))}
            <div className="ml-auto flex gap-1.5 flex-wrap">
              <Chip label="jours actifs" value={days.length} />
              <Chip label="conversations" value={monthTotals.conv} />
              {hasOutreach && <Chip label="mails" value={monthTotals.mail} />}
              {monthTotals.li > 0 && <Chip label="linkedin" value={monthTotals.li} />}
              <Chip label="bookés" value={monthTotals.rdv} />
            </div>
          </div>

          {loading ? <SkeletonRows count={6} /> : days.length === 0 ? (
            <EmptyState title="Aucune activité ce mois-ci" hint="Change de mois avec les flèches ci-dessus." />
          ) : mode === 'jour' ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-4 items-start">
              {/* Calendrier */}
              <div className="bg-panel border border-line rounded-card p-4">
                <div className="grid grid-cols-5 gap-1 mb-1">
                  {JOURS.map((j, i) => <div key={i} className="text-center font-mono text-[9px] uppercase tracking-widest text-faint py-1">{j}</div>)}
                </div>
                <div className="grid grid-cols-5 gap-1">
                  {cells.map((c, i) => {
                    if (!c) return <div key={i} />;
                    const d = byDate.get(c);
                    const dayNum = Number(c.slice(8, 10));
                    const isSel = c === selected;
                    const isToday = c === iso(now);
                    return (
                      <button key={i} disabled={!d} onClick={() => d && setSelected(c)}
                        className={`aspect-square rounded-md border text-left p-1.5 relative disabled:opacity-40 disabled:cursor-default transition-colors ${
                          isSel ? 'bg-hook-rose text-white border-hook-rose' : `${d ? intensity(d.conversations) : ''} ${isToday ? 'border-ink' : 'border-line'} ${d ? 'hover:border-hook-rose cursor-pointer' : ''}`}`}>
                        <span className={`text-[11px] font-semibold ${isSel ? 'text-white' : ''}`}>{dayNum}</span>
                        {d && (
                          <span className="absolute bottom-1 left-1.5 right-1.5 flex items-center justify-between">
                            <span className={`font-mono text-[8.5px] ${isSel ? 'text-white/80' : 'text-dim'}`}>{d.conversations > 0 ? `${d.conversations}💬` : (d.mailSent ? '✉️' : d.liSent ? '💼' : '')}</span>
                            {d.rdv > 0 && <span className={`font-mono text-[8.5px] font-bold px-1 rounded ${isSel ? 'bg-white/25 text-white' : 'bg-hook-rose/15 text-hook-rose'}`}>{d.rdv}</span>}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
                <p className="font-mono text-[9px] text-faint mt-3">💬 conversations du jour · badge = RDV bookés. Clique un jour actif.</p>
              </div>

              {/* Détail du jour sélectionné */}
              <div className="bg-panel border border-line rounded-card p-5">
                {!sel ? <EmptyState title="Sélectionne une journée" hint="Clique un jour actif dans le calendrier." /> : (
                  <>
                    <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                      <h2 className="text-[15px] font-bold capitalize">{longDate(sel.date)}</h2>
                      <div className="flex gap-1.5 flex-wrap">
                        <Chip label="conversations" value={sel.conversations} />
                        {!!sel.mailSent && <Chip label="mails" value={sel.mailSent} />}
                        {!!sel.liSent && <Chip label="linkedin" value={sel.liSent} />}
                        <Chip label="bookés" value={sel.rdv} />
                      </div>
                    </div>
                    {(sel.mailSent || sel.liSent) ? <div className="text-[11px] text-dim bg-panel-2 border border-line rounded-md px-2.5 py-1.5 mb-2">📧 {sel.mailSent ?? 0} mails · 💼 {sel.liSent ?? 0} messages LinkedIn envoyés ce jour</div> : null}
                    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mb-2">Résumés des conversations · {sel.connected} connectés · {pct(sel.rdv, sel.conversations)}% conversation→booké</div>
                    <div className="max-h-[56vh] overflow-y-auto -mx-1 px-1">
                      <CallSummaries items={sel.items} />
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            /* Vue semaine (GTM) */
            <div className="space-y-3">
              {weeks.map(w => (
                <div key={w.date} className="bg-panel border border-line rounded-card p-5">
                  <div className="flex items-start justify-between gap-2 mb-3 flex-wrap">
                    <h2 className="text-[15px] font-bold">Semaine du {new Date(w.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}</h2>
                    <div className="flex gap-1.5 flex-wrap">
                      <Chip label="conversations" value={w.conversations} />
                      {!!(w as any).mailSent && <Chip label="mails" value={(w as any).mailSent} />}
                      {!!(w as any).liSent && <Chip label="linkedin" value={(w as any).liSent} />}
                      <Chip label="bookés" value={w.rdv} />
                      <Chip label="connectés" value={w.connected} />
                    </div>
                  </div>
                  <details className="group">
                    <summary className="cursor-pointer list-none font-mono text-[10px] uppercase tracking-widest text-hook-rose">
                      Voir les résumés des conversations de la semaine ({w.items.length}) <span className="group-open:hidden">▾</span><span className="hidden group-open:inline">▴</span>
                    </summary>
                    <div className="mt-3 border-t border-line pt-3 max-h-[52vh] overflow-y-auto -mx-1 px-1">
                      <CallSummaries items={w.items} />
                    </div>
                  </details>
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 text-[11px] text-dim bg-panel-2 border border-line rounded-lg p-3 leading-relaxed">
            <span className="font-semibold">L'unité qui compte, c'est la conversation.</span> Une <b>conversation</b> = un appel décroché
            de plus de 30 s de discussion — c'est ce qu'on produit et ce qu'on suit. Un <b>booké</b> = un rendez-vous pris.
            {hasOutreach && <> Côté digital, <b>✉️ mails</b> et <b>💼 messages LinkedIn</b> = les volumes réellement envoyés pour vous (mesurés depuis nos outils d'envoi).</>}
            <br />
            Tout est <b>mesuré</b> automatiquement à partir des appels réels. On ne compte pas les numéros simplement
            composés (tentatives sans discussion) : seules les vraies conversations sont retenues.
          </div>
        </>
      )}
    </div>
  );
};

export default PortalJourneesPage;
